import { SEARCH_QUERY_PARAM } from '../../constants/config'

export type GalleryFilterType = 'image' | 'video'

export interface GalleryFilters {
  query?: string
  albumId?: string
  personIds?: Array<string>
  ownerIds?: Array<string>
  deviceId?: string
  type?: GalleryFilterType
  /** local day "YYYY-MM-DD" */
  dateFrom?: string
  /** local day "YYYY-MM-DD", inclusive */
  dateTo?: string
}

// `q` is shared with the navbar search (SEARCH_QUERY_PARAM); the rest mirror the API params.
const PARAM_KEYS = {
  albumId: 'album_id',
  personIds: 'person_id',
  ownerIds: 'user_id',
  deviceId: 'device_id',
  type: 'type',
  dateFrom: 'date_from',
  dateTo: 'date_to',
} as const

const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const dayOrUndefined = (value: string | null): string | undefined =>
  value && DAY_KEY_PATTERN.test(value) ? value : undefined

const typeOrUndefined = (value: string | null): GalleryFilterType | undefined =>
  value === 'image' || value === 'video' ? value : undefined

export const parseGalleryFilters = (search: string): GalleryFilters => {
  const params = new URLSearchParams(search)
  const personIds = params.getAll(PARAM_KEYS.personIds)
  const ownerIds = params.getAll(PARAM_KEYS.ownerIds)

  return {
    query: params.get(SEARCH_QUERY_PARAM) || undefined,
    albumId: params.get(PARAM_KEYS.albumId) || undefined,
    personIds: personIds.length ? personIds : undefined,
    ownerIds: ownerIds.length ? ownerIds : undefined,
    deviceId: params.get(PARAM_KEYS.deviceId) || undefined,
    type: typeOrUndefined(params.get(PARAM_KEYS.type)),
    dateFrom: dayOrUndefined(params.get(PARAM_KEYS.dateFrom)),
    dateTo: dayOrUndefined(params.get(PARAM_KEYS.dateTo)),
  }
}

/** Rewrites only the filter params; unrelated params in baseSearch are preserved. */
export const serializeGalleryFilters = (filters: GalleryFilters, baseSearch = ''): string => {
  const params = new URLSearchParams(baseSearch)

  params.delete(SEARCH_QUERY_PARAM)
  for (const key of Object.values(PARAM_KEYS)) params.delete(key)

  if (filters.query) params.set(SEARCH_QUERY_PARAM, filters.query)
  if (filters.albumId) params.set(PARAM_KEYS.albumId, filters.albumId)
  for (const id of filters.personIds ?? []) params.append(PARAM_KEYS.personIds, id)
  for (const id of filters.ownerIds ?? []) params.append(PARAM_KEYS.ownerIds, id)
  if (filters.deviceId) params.set(PARAM_KEYS.deviceId, filters.deviceId)
  if (filters.type) params.set(PARAM_KEYS.type, filters.type)
  if (filters.dateFrom) params.set(PARAM_KEYS.dateFrom, filters.dateFrom)
  if (filters.dateTo) params.set(PARAM_KEYS.dateTo, filters.dateTo)

  return params.toString()
}

/** Every selected person/owner counts as one active filter (mobile parity). */
export const countActiveFilters = (filters: GalleryFilters): number =>
  (filters.query ? 1 : 0) +
  (filters.albumId ? 1 : 0) +
  (filters.personIds?.length ?? 0) +
  (filters.ownerIds?.length ?? 0) +
  (filters.deviceId ? 1 : 0) +
  (filters.type ? 1 : 0) +
  (filters.dateFrom ? 1 : 0) +
  (filters.dateTo ? 1 : 0)

const parseDayKey = (dayKey: string): [number, number, number] => {
  const [year, month, day] = dayKey.split('-').map(Number)
  return [year, month, day]
}

/** start of the local day */
export const dateFromToApiDate = (dayKey: string): Date => {
  const [year, month, day] = parseDayKey(dayKey)
  return new Date(year, month - 1, day)
}

/** end of the local day, so the "to" date is inclusive (mobile parity) */
export const dateToToApiDate = (dayKey: string): Date => {
  const [year, month, day] = parseDayKey(dayKey)
  return new Date(new Date(year, month - 1, day + 1).getTime() - 1)
}
